import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { toast } from "sonner";
import { Building2, Plus, Trash2, X, Upload, Image as ImageIcon, Save } from "lucide-react";

function EditProfileModal({ pid, onClose, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/school-profiles/${pid}`); setDetail(data); }
    catch { toast.error("Gagal memuat profil"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [pid]);

  const saveText = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/school-profiles/${pid}`, { name: detail.name, signatories: detail.signatories.map((s) => ({ name: s.name, title: s.title })) });
      toast.success("Tersimpan");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
    setSaving(false);
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/school-profiles/${pid}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDetail((d) => ({ ...d, logo_url: data.logo_url })); toast.success("Logo mitra diunggah"); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal unggah logo"); }
  };
  const removeLogo = async () => {
    try { await api.delete(`/admin/school-profiles/${pid}/logo`); setDetail((d) => ({ ...d, logo_url: null })); onSaved?.(); }
    catch { toast.error("Gagal menghapus logo"); }
  };
  const uploadSign = async (idx, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/school-profiles/${pid}/signature/${idx}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDetail((d) => { const s = [...d.signatories]; s[idx] = { ...s[idx], sign_url: data.sign_url }; return { ...d, signatories: s }; });
      toast.success("TTD diunggah"); onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal unggah TTD"); }
  };
  const removeSign = async (idx) => {
    try {
      await api.delete(`/admin/school-profiles/${pid}/signature/${idx}`);
      setDetail((d) => { const s = [...d.signatories]; s[idx] = { ...s[idx], sign_url: null }; return { ...d, signatories: s }; }); onSaved?.();
    } catch { toast.error("Gagal menghapus TTD"); }
  };
  const setSig = (idx, key, val) => setDetail((d) => { const s = [...d.signatories]; s[idx] = { ...s[idx], [key]: val }; return { ...d, signatories: s }; });

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="edit-profile-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-lg text-[#1A1C1A] flex items-center gap-2"><Building2 className="h-5 w-5 text-[#3E6B8A]" /> Kelola Profil Sekolah/Mitra</h2>
          <button onClick={onClose} data-testid="close-edit-profile-btn" className="p-1 text-[#9C9A94] hover:text-[#1A1C1A]"><X className="h-5 w-5" /></button>
        </div>
        {loading || !detail ? <div className="text-sm text-[#9C9A94]">Memuat…</div> : (
          <div className="space-y-5">
            <div>
              <label className="text-xs font-medium text-[#5C5F5C]">Nama Sekolah/Mitra</label>
              <input value={detail.name} onChange={(e) => setDetail((d) => ({ ...d, name: e.target.value }))} data-testid="profile-name-input"
                className="w-full mt-1 h-10 px-3 rounded-xl border border-[#E5E0D8] outline-none focus:border-[#E25E3E]" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5F5C] block mb-1">Logo Mitra (muncul di KANAN kop; logo Talenta otomatis di kiri, "BIMBEL TALENTA" di tengah)</label>
              <div className="flex items-center gap-3">
                <div className="h-20 w-20 rounded-xl border border-dashed border-[#E5E0D8] bg-[#FBF9F5] flex items-center justify-center overflow-hidden">
                  {detail.logo_url ? <img src={detail.logo_url} alt="logo" data-testid="profile-logo-preview" className="max-h-full max-w-full object-contain" /> : <ImageIcon className="h-6 w-6 text-[#C9C6BE]" />}
                </div>
                <label className="text-xs px-3 py-2 rounded-full bg-[#3E6B8A] text-white cursor-pointer inline-flex items-center gap-1.5" data-testid="upload-logo-label">
                  <Upload className="h-3.5 w-3.5" /> Unggah Logo
                  <input type="file" accept="image/*" className="hidden" data-testid="logo-file-input" onChange={(e) => uploadLogo(e.target.files?.[0])} />
                </label>
                {detail.logo_url && <button onClick={removeLogo} data-testid="remove-logo-btn" className="text-xs text-[#D9381E] inline-flex items-center gap-1"><Trash2 className="h-3.5 w-3.5" /> Hapus</button>}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-[#5C5F5C] block mb-2">Tanda Tangan (4 penanda tangan — foto TTD muncul di atas nama)</label>
              <div className="grid sm:grid-cols-2 gap-3">
                {detail.signatories.map((s, i) => (
                  <div key={i} className="clay-card p-3 space-y-2" data-testid={`signatory-${i}`}>
                    <div className="h-16 rounded-lg border border-dashed border-[#E5E0D8] bg-[#FBF9F5] flex items-center justify-center overflow-hidden">
                      {s.sign_url ? <img src={s.sign_url} alt="ttd" data-testid={`sign-preview-${i}`} className="max-h-full max-w-full object-contain" /> : <span className="text-[10px] text-[#C9C6BE]">Belum ada TTD</span>}
                    </div>
                    <input value={s.name} onChange={(e) => setSig(i, "name", e.target.value)} data-testid={`sign-name-${i}`} placeholder="Nama"
                      className="w-full h-8 px-2 text-xs rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E]" />
                    <input value={s.title} onChange={(e) => setSig(i, "title", e.target.value)} data-testid={`sign-title-${i}`} placeholder="Jabatan"
                      className="w-full h-8 px-2 text-xs rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E]" />
                    <div className="flex items-center gap-2">
                      <label className="text-[11px] px-2.5 py-1.5 rounded-full bg-[#3E6B8A] text-white cursor-pointer inline-flex items-center gap-1" data-testid={`upload-sign-label-${i}`}>
                        <Upload className="h-3 w-3" /> Unggah TTD
                        <input type="file" accept="image/*" className="hidden" data-testid={`sign-file-input-${i}`} onChange={(e) => uploadSign(i, e.target.files?.[0])} />
                      </label>
                      {s.sign_url && <button onClick={() => removeSign(i)} data-testid={`remove-sign-${i}`} className="text-[11px] text-[#D9381E] inline-flex items-center gap-1"><Trash2 className="h-3 w-3" /> Hapus</button>}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-[#9C9A94] mt-2">Tip: unggah PNG latar transparan agar TTD menyatu rapi di atas nama.</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 rounded-full border border-[#E5E0D8] text-sm text-[#5C5F5C]">Tutup</button>
              <button onClick={saveText} disabled={saving} data-testid="save-profile-btn" className="px-4 py-2 rounded-full bg-[#1A1C1A] text-white text-sm inline-flex items-center gap-1.5 disabled:opacity-50"><Save className="h-4 w-4" /> {saving ? "Menyimpan…" : "Simpan Nama & Jabatan"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminProfilSekolah() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/school-profiles"); setProfiles(data.profiles || []); }
    catch { toast.error("Gagal memuat profil"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) { toast.error("Isi nama sekolah/mitra"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/admin/school-profiles", { name: newName.trim() });
      setNewName(""); toast.success("Profil dibuat"); await load(); setEditId(data.profile.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal membuat profil"); }
    setCreating(false);
  };
  const remove = async (p) => {
    if (!window.confirm(`Hapus profil "${p.name}"? Siswa yang tertaut akan kembali ke tampilan default.`)) return;
    try { await api.delete(`/admin/school-profiles/${p.id}`); toast.success("Profil dihapus"); load(); }
    catch { toast.error("Gagal menghapus"); }
  };

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto" data-testid="admin-profil-sekolah">
        <h1 className="font-heading font-black text-3xl text-[#1A1C1A] mb-1">Profil Sekolah/Mitra</h1>
        <p className="text-[#5C5F5C] mb-4">Tiap profil = 1 logo mitra + 4 tanda tangan untuk kop &amp; TTD Laporan Bulanan. Tautkan siswa ke profil di halaman Kelola Akun.</p>

        <div className="clay-card p-4 mb-4 flex items-center gap-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="new-profile-name-input" placeholder="Nama sekolah/mitra, mis. SMA Unggul Del"
            className="flex-1 h-10 px-3 rounded-xl border border-[#E5E0D8] outline-none focus:border-[#E25E3E]" />
          <button onClick={create} disabled={creating} data-testid="create-profile-btn"
            className="px-4 py-2 rounded-full bg-[#3E6B8A] text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50"><Plus className="h-4 w-4" /> Buat</button>
        </div>

        <div className="clay-card divide-y divide-[#F0ECE4]">
          {loading && <div className="p-5 text-sm text-[#9C9A94]">Memuat…</div>}
          {!loading && profiles.length === 0 && <div className="p-5 text-sm text-[#9C9A94]">Belum ada profil. Buat profil pertama Anda.</div>}
          {profiles.map((p) => (
            <div key={p.id} className="flex items-center gap-3 p-4" data-testid={`profile-row-${p.id}`}>
              <div className="h-12 w-12 rounded-xl border border-[#E5E0D8] bg-[#FBF9F5] flex items-center justify-center overflow-hidden shrink-0">
                <Building2 className="h-5 w-5 text-[#3E6B8A]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[#1A1C1A] truncate">{p.name}</div>
                <div className="text-xs text-[#9C9A94]">{p.has_logo ? "Logo ✔" : "Logo –"} · TTD: {p.signatories.filter((s) => s.has_sign).length}/{p.signatories.length}</div>
              </div>
              <button onClick={() => setEditId(p.id)} data-testid={`edit-profile-${p.id}`} className="text-xs px-3 py-1.5 rounded-full bg-[#1A1C1A] text-white font-medium">Kelola</button>
              <button onClick={() => remove(p)} data-testid={`delete-profile-${p.id}`} className="p-2 rounded-lg text-[#D9381E] hover:bg-[#D9381E]/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
      {editId && <EditProfileModal pid={editId} onClose={() => setEditId(null)} onSaved={load} />}
    </AppShell>
  );
}
